import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Button, Divider, Rating, Avatar, Stack, CircularProgress, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { startChat } from '../../../actions/chat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { urlImg, textoDuracion } from '../../common/helpers';
import { useIrAEntrar } from '../../../utils/destinoAlEntrar';
import { dinero } from '../../../utils/dinero';

const PAGE_SIZE = 5;

const PageServiceList = ({ services, ownerName, irA, ownerId, categories, ownerAvatar, isOwner, pageId }) => {
  const dispatch = useDispatch();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const isAuthenticated = useSelector(state => state.user.loading === false && state.user.user._id);
  // Sin sesión, «Cotizar» manda a entrar; al entrar regresa a esta página
  const irAEntrar = useIrAEntrar();

  const handleNavigate = (service) => {
    const userId = service.user || service.userId || ownerId;
    irA(`/servicio/${userId}/${service._id}`);
  };

  const handleQuote = () => {
    console.log('--- Debug Cotizar ---');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('ownerId:', ownerId);
    console.log('isOwner:', isOwner);
    console.log('pageId:', pageId);

    if (!isAuthenticated) {
      console.warn('Usuario no autenticado, redirigiendo...');
      irAEntrar();
      return;
    }

    if (ownerId && !isOwner) {
      console.log('Iniciando chat...');
      const initialMsg = `Hola, me interesa contratar un servicio personalizado. Vi tu perfil de "${ownerName}" y me gustaría cotizar. ¿Podrías darme un presupuesto?`;
      dispatch(startChat(ownerId, pageId, 'page', initialMsg, `${ownerId}_${pageId}`));
    } else {
      console.warn('No se puede cotizar: ownerId faltante o es el dueño');
    }
  };

  const onlineUsers = useSelector(state => state.chat.onlineUsers);
  // Sin sesión el servidor no manda quién está conectado (y con sesión tarda un instante): mientras tanto no se dice nada
  const presenciaRecibida = useSelector(state => state.chat.presenciaRecibida);
  const isOnline = ownerId && onlineUsers.includes(ownerId);

  const visibleServices = (services || []).slice(0, visibleCount);
  const hasMore = visibleCount < (services || []).length;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [services]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount(prev => prev + PAGE_SIZE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, visibleCount]);

  const getCategoryName = (service) => {
    // nombre resuelto por el backend (funciona aunque la categoría esté deshabilitada)
    if (service?.categoriaNombre) return service.categoriaNombre;
    const catId = service?.categoria;
    if (!catId || !categories) return "Servicio";
    const found = categories.find(c => c._id === catId);
    return found ? found.name : "Servicio";
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
        Listado de servicios
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Conoce la lista de servicios disponibles
      </Typography>

      {visibleServices.map((service) => (
        <Paper
          key={service._id}
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: '16px',
            border: 1, borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 10px 25px rgba(0,0,0,0.05)'
            }
          }}
        >
          <Box
            onClick={() => handleNavigate(service)}
            sx={{
              width: { xs: '100%', sm: '240px' },
              height: '160px',
              borderRadius: '12px',
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
              cursor: 'pointer'
            }}
          >
            <img
              src={service.img && service.img.length > 0 ? `${urlImg}/servicios/${service._id}/${service.img[0].nombre}` : 'https://via.placeholder.com/300x200'}
              alt={service.nombre}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {service.facilitadores && service.facilitadores.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: -1, // Stack avatars slightly
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  px: 1,
                  py: 0.5,
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  backdropFilter: 'blur(4px)'
                }}
              >
                {service.facilitadores.slice(0, 3).map((fac, idx) => (
                  <Avatar 
                    key={fac._id}
                    src={`${urlImg}/w_40,h_40,c_fill/v${fac.versionAvatar}/users/${fac._id}/${fac.avatar}`}
                    sx={{ 
                      width: 18, 
                      height: 18, 
                      border: '1px solid white',
                      marginLeft: idx > 0 ? '-6px' : 0
                    }}
                  />
                ))}
                {service.facilitadores.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', ml: 0.5 }}>
                    +{service.facilitadores.length - 3}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.65rem', ml: 1 }}>
                  {service.facilitadores.length === 1 ? service.facilitadores[0].name : `${service.facilitadores.length} Pro`}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.7),
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: '20px',
                fontSize: '0.7rem',
                fontWeight: 600,
                backdropFilter: 'blur(4px)'
              }}
            >
              {getCategoryName(service)}
            </Box>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box onClick={() => handleNavigate(service)} sx={{ cursor: 'pointer' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {service.nombre}
                </Typography>
                {/* Solo en la vista previa (dueño o persona invitada): el público todavía no ve este servicio */}
                {service.enEspera && (
                  <Chip
                    size="small"
                    label={service.enEspera === 'rechazado' ? 'Invitación rechazada · no visible' : 'Espera respuesta · no visible'}
                    sx={{ bgcolor: service.enEspera === 'rechazado' ? 'error.main' : 'warning.main', color: 'primary.main', fontWeight: 700, mb: 0.5 }}
                  />
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {service.facilitadores && service.facilitadores.length > 0 
                    ? `Lo imparte ${service.facilitadores.map(f => f.name).join(', ')}`
                    : ownerName}
                </Typography>
              </Box>
              {service.ratingPromedio > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Rating value={service.ratingPromedio} precision={0.5} readOnly size="small" sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {service.ratingPromedio.toFixed(1)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Nuevo
                </Typography>
              )}
            </Box>

            {service.descripcion && (
              <Typography variant="body2" sx={{
                color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word'
              }}>
                {service.descripcion}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <LocationOnIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">CDMX, MX</Typography>
              </Box>
              {textoDuracion(service.duracionEstimada, service.unidadDuracion) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">{textoDuracion(service.duracionEstimada, service.unidadDuracion)}</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
                  {dinero(service.costoServicio)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  por {service.tipoCosto}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => handleNavigate(service)}
                sx={{
                  textTransform: 'none',
                  bgcolor: 'primary.main',
                  borderRadius: '8px',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                Ver detalle
              </Button>
            </Box>
          </Box>
        </Paper>
      ))}

      {hasMore && (
        <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          mt: 6,
          p: '12px 24px', 
          borderRadius: '12px', 
          bgcolor: 'white', 
          border: 1, borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
            No encuentras el servicio que necesitas, solicita una cotización
          </Typography>
          <Button
            variant="contained"
            onClick={handleQuote}
            disabled={isOwner}
            sx={{ 
              bgcolor: isOwner ? 'divider' : 'primary.main', 
              textTransform: 'none', 
              px: 4, 
              borderRadius: '8px',
              fontWeight: 'bold',
              '&.Mui-disabled': {
                bgcolor: 'divider',
                color: 'text.secondary'
              }
            }}
          >
            Cotizar
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar 
            src={ownerAvatar} 
            sx={{ width: 40, height: 40, border: 2, borderColor: 'sutil' }} 
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
              {ownerName}
            </Typography>
            {presenciaRecibida && (
              <Typography variant="caption" sx={{ color: isOnline ? 'success.oscuro' : 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, bgcolor: isOnline ? 'success.main' : 'text.secondary', borderRadius: '50%' }} />
                {isOnline ? 'En línea' : 'Desconectado'}
              </Typography>
            )}
          </Box>
          <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20, ml: 1 }} />
        </Stack>
      </Paper>
    </Box>
  );
};

export default PageServiceList;
